import { useCallback, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Dropdown from 'components/Dropdown'
import Button from 'components/Button'
import { DropDownItem } from 'types/components'
import {
  CampaignUpdateFormSchemas,
  CampaignUpdateFormTypes,
  CampaignComponentsAPITypes,
} from 'schemas/campaign-voucher-schemas'
import { useCampaignUpdate } from './use-campaign-update'
import { getCampaignsAPI, getCampaignComponentsAPI } from 'api/campaign-voucher'
import { useCampaignsSelectQuery } from './use-campaign-update'

import { toast } from 'react-toastify'

import { useQueryClient } from 'react-query'
import { QUERY_KEYS } from "constants/querykeys";

export const UpdateCampaignForm = ({
  onCloseModal,
}: {
  onCloseModal: () => void
}) => {
  const [selectedCampaign, setSelectedCampaign] = useState<{ label: string; value: string } | null>(null);
  const [inputValue, setInputValue] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [campaignTemplates, setCampaignTemplates] = useState<any[]>([])
  const { campaignOptions } = useCampaignsSelectQuery()
  const [filteredOptions, setFilteredOptions] = useState(campaignOptions)
  const [loading, setLoading] = useState(true)
  const [campaignComponents, setCampaignComponents] = useState<CampaignComponentsAPITypes['data']>([])
  const [componentsLoading, setComponentsLoading] = useState(false)

  // fetch templates on mount and keep them in state
  useEffect(() => {
    let mounted = true
    async function fetchTemplates() {
      setLoading(true)
      try {
        const response = await getCampaignsAPI()
        const templates = response.data || []
        if (!mounted) return
        setCampaignTemplates(templates)
        // if user already selected a campaign (unlikely on mount), populate it
        if (selectedCampaign) {
          const tpl = templates.find(t => String(t.campaignName) === String(selectedCampaign.label))
          if (tpl) populateFormFromTemplate(tpl)
        }
      } finally {
        setLoading(false)
      }
    }
    fetchTemplates()
    return () => { mounted = false }
    // eslint-disable-next-line
  }, [])

  // helper to fetch components for selected campaign
  const fetchCampaignComponents = async (campaignId?: number) => {
    if (!campaignId) {
      setCampaignComponents([])
      return
    }
    setComponentsLoading(true)
    try {
      const resp = await getCampaignComponentsAPI(campaignId)
      setCampaignComponents(resp.data || [])
    } catch (err) {
      setCampaignComponents([])
    } finally {
      setComponentsLoading(false)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const populateFormFromTemplate = (campaign: any) => {
    form.reset({
      campaignId: campaign.campaignId,
      campaignName: campaign.campaignName,
      campaignCode: campaign.campaignCode ?? '',
      externalReference: campaign.externalReference ?? '',
      description: campaign.description,
      statusId: campaign.statusId ?? 0,
      startDate: campaign.startDate?.split('T')[0] ?? '',
      endDate: campaign.endDate?.split('T')[0] ?? '',
    })
    // fetch components for this campaign
    fetchCampaignComponents(campaign.campaignId)
  }

  const formDefaultValues: CampaignUpdateFormTypes = {
    campaignId: 0,
    campaignName: '',
    campaignCode: '',
    externalReference: '',
    description: '',
    statusId: 0,
    startDate: '',
    endDate: '',
  }

  const form = useForm<CampaignUpdateFormTypes>({
    resolver: zodResolver(CampaignUpdateFormSchemas),
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    if (!inputValue) {
      setFilteredOptions(campaignOptions)
    } else {
      const filtered = campaignOptions.filter(opt =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
      )
      setFilteredOptions(filtered)
    }
  }, [inputValue, campaignOptions])

  const {
    mutate: mutateCampaignUpdate,
    isLoading: isLoadingCampaignUpdate,
  } = useCampaignUpdate()

  const queryClient = useQueryClient();

  const onSubmit = useCallback(
    (data: CampaignUpdateFormTypes) => {
      const payload = {
        ...data,
        startDate: data.startDate ? `${data.startDate} 00:00:00` : null,
        endDate: data.endDate ? `${data.endDate} 23:59:59` : null,
      }

      // console.log('Submitting payload:', payload)

      mutateCampaignUpdate(
        { formData: payload },
        {
          async onSuccess() {
            toast.success('Campaign updated successfully!')
            onCloseModal()

            await queryClient.invalidateQueries({
              predicate: (query) => query.queryKey[0] === QUERY_KEYS.VOUCHER_LIST
            })
          },
          onError: (error) => {
            const message =
              typeof error === 'object' && error !== null
                ? (error as { outcomeUserMessage?: string; outcomeMessage?: string })
                  .outcomeUserMessage ||
                (error as { outcomeMessage?: string }).outcomeMessage ||
                'Failed to update campaign.'
                : String(error)
            toast.error(message)
          },
        }
      )
    },
    [mutateCampaignUpdate, onCloseModal, queryClient]
  )

  const STATUS_TYPES_OPTIONS = [
    { label: 'Inactive', value: 0 },
    { label: 'Active', value: 1 },
  ]

  // const formValues = form.watch()
  // useEffect(() => {
  //   console.log("Current form values:", formValues)
  // }, [formValues])

  if (loading) return <div className="p-4">Loading...</div>

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4">
        <Controller
          name="campaignId"
          control={form.control}
          render={({ field }) => (
            <Dropdown
              title="Select Campaign"
              options={filteredOptions}
              value={selectedCampaign}
              onSelect={(option: DropDownItem | null) => {
                setSelectedCampaign(option)
                field.onChange(option ? Number(option.value) : null)
                if (option) {
                  const tpl = campaignTemplates.find(t => String(t.campaignId) === String(option.value))
                  if (tpl) populateFormFromTemplate(tpl)
                }
              }}
              className="w-full tracking-wider"
              borderColor="#C4C4C4"
              containerClassName="focus:border-primary"
              placeholder="Search Campaigns..."
              isClearable
              isSearchable
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="campaignName"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="campaignName"
              label="Campaign Name"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="campaignCode"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="campaignCode"
              label="Campaign Code"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="externalReference"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="externalReference"
              label="External Reference"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="description"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="description"
              label="Description"
              containerClassName="flex-1"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="statusId"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              title="Status"
              value={STATUS_TYPES_OPTIONS.find(
                (option) => option.value === value
              ) || null}
              options={STATUS_TYPES_OPTIONS.map((option) => ({
                ...option,
                value: String(option.value),
              }))}
              className="w-64 tracking-wider mb-5"
              borderColor="#e8ebef"
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
            />
          )}
        />
        {form.formState.errors.statusId && (
          <span className="text-red text-sm">
            {form.formState.errors.statusId.message}
          </span>
        )}
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>

      {/* Component Information */}
      <div className="mb-4">
        <p className="block text-sm font-medium text-gray-700 mb-2">Component Information</p>
        {componentsLoading ? (
          <div>Loading components...</div>
        ) : campaignComponents && campaignComponents.length > 0 ? (
          <div className="overflow-auto border rounded">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium bg-gray-100 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium bg-gray-100 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium bg-gray-100 uppercase tracking-wider">Start Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium bg-gray-100 uppercase tracking-wider">End Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaignComponents.map((comp) => (
                  <tr key={comp.componentId}>
                    <td className="px-4 py-2 align-top max-w-xs">
                      <div className="whitespace-normal break-words text-sm text-gray-700">{comp.componentName || '—'}</div>
                    </td>
                    <td className="px-4 py-2 align-top text-sm text-gray-700">{comp.entityTypeName ?? '—'}</td>
                    <td className="px-4 py-2 align-top text-sm text-gray-700">{comp.startDate ? String(comp.startDate).split('T')[0] : '—'}</td>
                    <td className="px-4 py-2 align-top text-sm text-gray-700">{comp.endDate ? String(comp.endDate).split('T')[0] : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-gray-400">No vouchers or campaigns are attached to this campaign.</div>
        )}
      </div>

      <div className="flex justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-2 rounded bg-primary"
          disabled={isLoadingCampaignUpdate}
        >
          <span className="tracking-wider font-bold">
            {isLoadingCampaignUpdate ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>
        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>
    </form >
  )
}